import React from 'react';
import { FileTextIcon, PlusIcon, XCircleIcon } from 'lucide-react';

interface TemplateEmptyStateProps {
  hasFilters: boolean;
  onCreateClick: () => void;
  onClearFilters: () => void;
}

export function TemplateEmptyState({
  hasFilters,
  onCreateClick,
  onClearFilters,
}: TemplateEmptyStateProps) {
  if (hasFilters) {
    // No results for current search/filters
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircleIcon className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-[#212121] mb-2">
          No templates found
        </h3>
        <p className="text-gray-600 mb-6">
          No templates match your current search or filters.
          <br />
          Try adjusting your search or create a new template.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onClearFilters}
            className="px-6 py-3 rounded-xl font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={onCreateClick}
            className="px-6 py-3 rounded-xl font-semibold bg-[#5B8E87] text-white hover:bg-[#4F7A74] transition-colors flex items-center justify-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            Create Template
          </button>
        </div>
      </div>
    );
  }

  // No templates at all - first time user
  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
      <div className="w-20 h-20 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
        <FileTextIcon className="w-10 h-10 text-[#5B8E87]" />
      </div>
      <h3 className="text-lg font-semibold text-[#212121] mb-2">
        Create your first template
      </h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Save time by creating message templates for common scenarios.
        <br />
        Perfect for greetings, follow-ups, and quick responses.
      </p>

      {/* Benefits List */}
      <div className="text-left max-w-sm mx-auto mb-6 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#5B8E87] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-[#5B8E87] text-sm">✓</span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              Use variables like {'{'}name{'}'} and {'{'}goal{'}'}
            </p>
            <p className="text-xs text-gray-500">Auto-fill with client data</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#5B8E87] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-[#5B8E87] text-sm">✓</span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              Add images and media
            </p>
            <p className="text-xs text-gray-500">Share meal plans and charts</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#5B8E87] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-[#5B8E87] text-sm">✓</span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              Organize with categories and tags
            </p>
            <p className="text-xs text-gray-500">Find templates instantly</p>
          </div>
        </div>
      </div>

      <button
        onClick={onCreateClick}
        className="bg-[#5B8E87] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors inline-flex items-center gap-2"
      >
        <PlusIcon className="w-5 h-5" />
        Create Your First Template
      </button>
    </div>
  );
}
